import React, { useState, useRef } from 'react';
import gateApi from '../api';
import {
  C, FormShell, Field, TextInput, Select, SubmitButton, Banner,
} from './FormKit';

const LOAD_TYPES = ['10mm', '20mm', '30mm', '40mm', '60mm', 'DUST', 'BOULDER', 'WMM', 'GSB', 'BC-1-Dust', 'OTHER'];

// datetime-local wants "yyyy-MM-ddTHH:mm" in the browser's own clock, which at
// the gate is the yard's clock. Built by hand rather than from toISOString(),
// which converts to UTC and would show the operator a time five and a half
// hours off their own wall.
function localNow() {
  const d = new Date();
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Charity dispatch — stone given away rather than sold.
 *
 * Deliberately not part of the gate flow. These vehicles are not weighed,
 * billed or photographed, and the form is usually filled after the vehicle has
 * already gone, so it asks for the exit time instead of stamping one. Nothing
 * here touches a yard transaction, and nothing here reaches the sold tonnage
 * or revenue figures.
 */
export default function CharityForm() {
  const [plate, setPlate]       = useState('');
  const [material, setMaterial] = useState('');
  const [fromPlace, setFrom]    = useState('');
  const [toPlace, setTo]        = useState('');
  const [exitTime, setExitTime] = useState(localNow);
  const [busy, setBusy]         = useState(false);
  const [result, setResult]     = useState(null);
  const plateRef = useRef(null);

  function reset(keepPlaces) {
    setPlate('');
    setMaterial('');
    setExitTime(localNow());
    // A run of donations usually goes from the same pile to the same site, so
    // the two places are kept and only the vehicle and load are cleared.
    if (!keepPlaces) { setFrom(''); setTo(''); }
    // TextInput is a plain function component, so the ref goes on a wrapper and
    // the input is found inside it — the same way the entry form does it.
    setTimeout(() => plateRef.current?.querySelector('input')?.focus(), 50);
  }

  async function submit() {
    const cleanPlate = plate.toUpperCase().replace(/\s+/g, '');
    if (!cleanPlate) { setResult({ ok: false, msg: 'Enter the vehicle number' }); return; }
    if (!material)   { setResult({ ok: false, msg: 'Choose the material' }); return; }
    if (!exitTime)   { setResult({ ok: false, msg: 'Enter the time the vehicle left' }); return; }

    setBusy(true);
    setResult(null);
    try {
      await gateApi.recordCharityDispatch({
        licensePlate: cleanPlate,
        material,
        loadingFrom: fromPlace.trim(),
        destination: toPlace.trim(),
        exitTime,
      });
      setResult({ ok: true, msg: `${cleanPlate} recorded — ${material}${toPlace.trim() ? ` to ${toPlace.trim()}` : ''}` });
      reset(true);
    } catch (err) {
      setResult({
        ok: false,
        msg: err?.response?.data?.message || 'Could not save. Check the details and try again.',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <FormShell title="Charity vehicle" accent="#5B21B6">
      {result && <Banner type={result.ok ? 'success' : 'error'}>{result.msg}</Banner>}

      <div style={{
        background: C.panel2, border: `1px solid ${C.border}`, borderRadius: 10,
        padding: '13px 16px', marginBottom: 20, fontSize: 13, color: C.muted, lineHeight: 1.5,
      }}>
        For stone given away — no weighing, no challan, no payment. These loads
        are kept separate and never counted in sales.
      </div>

      <Field label="Vehicle number">
        <div ref={plateRef}>
          <TextInput value={plate} onChange={setPlate} placeholder="BR03GC0559" mono autoFocus
                     style={{ textTransform: 'uppercase' }} />
        </div>
      </Field>

      <Field label="Material">
        <Select value={material} onChange={setMaterial} options={LOAD_TYPES}
                placeholder="— choose material —" />
      </Field>

      <Field label="Loading from" hint="The pile, crusher or stockyard it was loaded at">
        <TextInput value={fromPlace} onChange={setFrom} placeholder="e.g. Plant 1 stockyard" />
      </Field>

      <Field label="Going to" hint="Who it was given to, or where it was delivered">
        <TextInput value={toPlace} onChange={setTo} placeholder="e.g. Shiv Mandir road, Bela" />
      </Field>

      <Field label="Exit time" hint="When the vehicle actually left — change it if you are entering this later">
        <TextInput value={exitTime} onChange={setExitTime} type="datetime-local" mono />
      </Field>

      <SubmitButton onClick={submit} disabled={busy} color="#5B21B6">
        {busy ? 'Saving…' : 'Record charity vehicle'}
      </SubmitButton>

      <button onClick={() => reset(false)} style={{
        width: '100%', background: 'none', border: 'none', marginTop: 14,
        color: C.faint, fontFamily: C.mono, fontSize: 12, cursor: 'pointer',
      }}>Clear the whole form</button>
    </FormShell>
  );
}
